import { el, clear } from "../lib/dom";
import { getGame } from "../games/registry";
import { recordLevelResult, getProgress, getCosmetics, unlockLevel } from "../lib/storage";
import { getMascotEmoji, getMascotSvg } from "../shop/catalog";
import { modal, starRow, toast } from "./components";
import { navigate } from "./router";
import type { Screen } from "./home";
import type { LevelView } from "../games/types";

export function playScreen(gameId: string, levelNum: number): Screen {
  const game = getGame(gameId);
  if (!game) {
    return { node: el("div.screen", {}, el("p", {}, "Onbekend spel."), el("button.btn", { onclick: () => navigate("/") }, "Home")) };
  }
  const level = Math.min(Math.max(1, levelNum), game.levelCount);

  function skipLevel(): void {
    if (level < game!.levelCount) {
      unlockLevel(gameId, level + 1);
      navigate(`/g/${gameId}/${level + 1}`);
    } else {
      navigate(`/g/${gameId}`);
    }
  }

  const status = el("span.play-status");
  const header = el(
    "header.app-header.play-header",
    { style: `--accent:${game.accent}` },
    el("button.icon-btn", { onclick: () => navigate(`/g/${gameId}`), "aria-label": "Terug" }, "‹"),
    el("div.play-title", {}, `${game.title}`, el("span.play-level", {}, `Level ${level}`)),
    status,
    el("button.icon-btn", { onclick: skipLevel, "aria-label": "Sla level over", title: "Overslaan" }, "⏭"),
  );

  const mount = el("div.play-area");
  const node = el("div.screen.play-screen", { style: `--accent:${game.accent}` }, header, mount);

  let view: LevelView | null = null;
  let closeModal: (() => void) | null = null;

  // The equipped mascot lounges in the empty space of the board, watching along.
  // It sits BEHIND the game (z-index / pointer-events handled in CSS) so it never
  // interferes; games clear the mount on (re)start, so we re-add it after each.
  function buildBuddy(): HTMLElement | null {
    const id = getCosmetics().equipped.mascot;
    const svg = getMascotSvg(id, false);
    const emoji = getMascotEmoji(id);
    if (svg) {
      const wrap = el("div.play-buddy.play-buddy-svg", { "aria-hidden": "true" });
      const cat = el("span.mascot-svg");
      cat.innerHTML = svg;
      wrap.append(cat);
      return wrap;
    }
    if (emoji) return el("div.play-buddy", { "aria-hidden": "true" }, emoji);
    return null;
  }

  function start(): void {
    closeModal?.();
    closeModal = null;
    clear(mount);
    view = game!.createLevelView({
      level,
      mount,
      requestExit: () => navigate(`/g/${gameId}`),
      setStatus: (t) => (status.textContent = t),
      onWin: handleWin,
      onLose: () => {},
    });
    const buddy = buildBuddy();
    if (buddy) mount.append(buddy);
  }

  function handleWin(result: { stars: number; score?: number; moves?: number }): void {
    const { firstClear, coinsEarned, streak, streakBumped } = recordLevelResult(gameId, level, result, game!.levelCount);
    const hasNext = level < game!.levelCount;

    const body = el("div.win-body");
    // The equipped mascot (shop cosmetic) celebrates along. Custom SVG mascots
    // (Pippie) show their happy state; emoji mascots keep the emoji + confetti.
    const mascotId = getCosmetics().equipped.mascot;
    const mascotSvg = getMascotSvg(mascotId, true);
    const mascotEmoji = getMascotEmoji(mascotId);
    if (mascotSvg) {
      const cat = el("span.mascot-svg");
      cat.innerHTML = mascotSvg;
      body.append(el("div.win-mascot.win-mascot-svg", {}, cat, el("span", {}, "🎉")));
    } else if (mascotEmoji) {
      body.append(el("div.win-mascot", {}, `${mascotEmoji} 🎉`));
    }
    body.append(starRow(result.stars, 3, "2.2rem"));
    const lines: string[] = [];
    if (typeof result.score === "number") lines.push(`Score: ${result.score}`);
    if (typeof result.moves === "number") lines.push(`Zetten: ${result.moves}`);
    if (coinsEarned > 0) lines.push(`🪙 +${coinsEarned} munten`);
    body.append(el("p.win-lines", {}, lines.join("  ·  ")));
    if (firstClear) body.append(el("p.win-sub", {}, "Nieuw level vrijgespeeld!"));
    // Celebrate a freshly-earned streak day.
    if (streakBumped) {
      body.append(el("p.win-streak", {}, streak <= 1 ? "🔥 Streak gestart!" : `🔥 ${streak} dagen op rij!`));
    }

    closeModal = modal({
      title: "Level voltooid! 🎉",
      body,
      buttons: hasNext
        ? [
            { label: "Volgende ›", primary: true, onClick: () => navigate(`/g/${gameId}/${level + 1}`) },
            { label: "Opnieuw", onClick: () => start() },
            { label: "Levels", onClick: () => navigate(`/g/${gameId}`) },
          ]
        : [
            { label: "Levels", primary: true, onClick: () => navigate(`/g/${gameId}`) },
            { label: "Opnieuw", onClick: () => start() },
          ],
    });
    void getProgress();
    if (coinsEarned > 0) toast(`+${coinsEarned} munten verdiend!`);
  }

  start();

  return {
    node,
    destroy() {
      closeModal?.();
      view?.destroy();
    },
  };
}
